import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { HelpButton } from '@/components/help/HelpButton';
import { cn } from '@/lib/utils';

export function Tabs({
  tabs,
  active,
  onChange,
  help = true,
}: {
  tabs: { id: string; label: string; badge?: ReactNode; icon?: LucideIcon }[];
  active: string;
  onChange: (id: string) => void;
  help?: boolean;
}) {
  return (
    <div className="mb-4 flex items-center gap-2">
      <div
        role="tablist"
        className="flex min-w-0 max-w-full items-center gap-1 overflow-x-auto rounded-lg border border-border/70 bg-muted/60 p-1"
      >
        {tabs.map((tab) => {
          const selected = active === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(tab.id)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-[color,background-color,box-shadow] duration-150 focus-ring',
                selected
                  ? 'bg-card text-foreground shadow-sm ring-1 ring-border/60'
                  : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
              )}
            >
              {Icon && <Icon className={cn('h-4 w-4', selected ? 'text-primary' : 'text-muted-foreground')} aria-hidden />}
              {tab.label}
              {tab.badge}
            </button>
          );
        })}
      </div>
      {help && <HelpButton tab={active} className="ms-auto shrink-0" />}
    </div>
  );
}
